import { useState } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  useTheme,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";

import { darkColors } from "../../../theme/colors";
import englishTranslations from "../../../i18n/translations/en";
import PlanetXDescription from "../../utils/footer-text";
import SocialAccountBar from "../../utils/social-account-bar";
import GradientButton from "../../utils/gradient-button";
import SocialIconsWithText from "../../utils/social-icons";
import FieldLabel from "../../utils/field-label";
import FieldInput from "../../utils/field-input";
import Logo from "../../utils/logo";
import Headings from "../../utils/headings";
import useWindowHeight from "../../../hooks/window-size/use-window-height";
import useWindowWidth from "../../../hooks/window-size/use-window-width";
import HeroImage from "../../utils/hero-image";
import BackgroundContainer from "../../utils/background-container";
import { useNavigate } from "react-router-dom";
import ConnectWallet from "./connect-wallet";
import Referral from "./referral";
import routes from "../../../constants/routes";
import useApiRequest from "../../../api/api";
import apiRoutes from "../../../api/routes";
import LoadingBackdrop from "../../utils/loader";
import { useGoogleAuth } from "../../utils/AuthServices";
import { useSnackbar } from "../../utils/SnackbarProvider";
import { useUser } from "../../../context/user-context";
import Otp from "../otp";
import Popup from "../../utils/popups/otp-popup";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import TermsPrivacyLinks from "../../utils/terms-privacy-links";
export default function SignUpForm() {
  const { makeApiCall } = useApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // State for password visibility

  const { handleGoogleLogin } = useGoogleAuth({
    isLoading: setIsLoading,
  });
  const showSnackbar = useSnackbar(); // Use the global Snackbar
  const [showConnectWallet, setShowConnectWallet] = useState(false);
  const { user, updateUser } = useUser();

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });
  const windowWidth = useWindowWidth();

  const isMobile = windowWidth < 1050;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleIconClick = (url) => {
    window.open(url, "_blank");
  };

  const handleTextClick = () => {
    navigate(routes.login);
  };

  const handleRegisterClick = async () => {
    setIsLoading(true);
    const payload = {
      username: formData.username,
      email: formData.email,
      password: formData.password,
      userTypeID: 2,
    };

    const response = await makeApiCall(
      apiRoutes.auth.register,
      "POST",
      payload
    );
    if (response.success) {
      // localStorage.setItem("user", JSON.stringify(response.data));
      // updateUser(response.data);
      // setShowConnectWallet(true);
      setIsLoading(false);
      setShowOTP(true);
    } else {
      setIsLoading(false);
      showSnackbar(response.message, "error");
    }
  };

  const handleSocialIconClick = (platform) => {
    if (platform === "google") {
      handleGoogleLogin();
    }
    // Add additional logic based on the platform clicked
  };
  const handleContinue = (data) => {
    localStorage.setItem("user", JSON.stringify(data));
    updateUser(data);
    navigate(routes.connect_wallet);
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <BackgroundContainer>
      <LoadingBackdrop isLoading={isLoading} />

      <Container maxWidth={false} sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            flexDirection: isMobile ? "column" : "row",
            minHeight: "100vh",
            gap: 4,
            py: 4,
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              flex: 1,
              maxWidth: isMobile ? "100%" : "30%",
              ml: "6%",
              pr: isMobile ? 4 : 0,
              py: 4,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between", // This spreads out the content
            }}
          >
            {/* Main Content */}
            <Box>
              <Logo />

              {/* Sign Up Form */}
              <Headings
                heading={englishTranslations.auth.signupHeading}
                subHeading={englishTranslations.auth.signupSubHeading}
              />

              <Stack spacing={2}>
                <FieldLabel text={englishTranslations.auth.name} />
                <FieldInput
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  type="text"
                />

                <FieldLabel text={englishTranslations.auth.email} />
                <FieldInput
                  label="mail@here.com"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="text"
                />

                <FieldLabel text={englishTranslations.auth.password} />
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FieldInput
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    type={showPassword ? "text" : "password"} // Toggle between text and password
                  />
                  <IconButton
                    onClick={togglePasswordVisibility}
                    sx={{ position: "absolute", right: 8 }}
                    className="white-icon"
                  >
                    {showPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: isMobile ? "flex-col" : "flex",
                    alignItems: "center",
                    gap: 2,
                    pt: 3,
                  }}
                >
                  <GradientButton
                    text={englishTranslations.auth.register}
                    onClick={handleRegisterClick}
                    width={isMobile ? "100%" : "auto"}
                  />
                  {!isMobile && (
                    <Typography
                      variant="body2"
                      sx={{
                        border: "1px solid",
                        borderLeft: "1px",
                        height: 50,
                        ml: 1,
                      }}
                    />
                  )}
                  {isMobile && (
                    <Divider sx={{ my: 3, background: darkColors.gray }} />
                  )}
                  <SocialIconsWithText
                    text={englishTranslations.auth.signupWith}
                    onIconClick={handleSocialIconClick}
                    isMobile={isMobile}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Footer Content - Now positioned at the bottom */}
            <Box sx={{ mt: "auto", pt: isMobile ? 0 : 4 }}>
              <SocialAccountBar
                text={englishTranslations.auth.account}
                onIconClick={handleIconClick}
                onTextClick={handleTextClick}
                isMobile={isMobile}
              />
              <Divider sx={{ my: 2, background: darkColors.gray }} />
              <PlanetXDescription isMobile={isMobile} />
              <TermsPrivacyLinks />
            </Box>
          </Box>

          {/* Right Section - Hero Image */}
          {/* <HeroImage /> */}
        </Box>
      </Container>
      <Popup open={showOTP} onClose={() => setShowOTP(false)}>
        <Otp
          handleBack={() => {
            setShowOTP(false);
          }}
          handleContinue={handleContinue}
          email={formData.email}
        />
      </Popup>
    </BackgroundContainer>
  );
}
